import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import LivePreview from "@/components/LivePreview";
import { projects as projectsApi, ai as aiApi, upload as uploadApi, payments as paymentsApi, templates as templatesApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Music, Camera, Film, Building2, Palette, Briefcase, User2, Video,
  Sparkles, Upload, X, ArrowLeft, ArrowRight, Loader2, Plus, Trash2,
  GripVertical, Check, Download, Wand2, Instagram, Youtube, Music2,
  Twitter, Facebook, Linkedin, Globe,
} from "lucide-react";

const CREATOR_TYPES = [
  { key: "Music Artist", icon: Music },
  { key: "Actor", icon: Video },
  { key: "Model", icon: User2 },
  { key: "Influencer", icon: Camera },
  { key: "Producer", icon: Music2 },
  { key: "Director", icon: Film },
  { key: "Photographer", icon: Camera },
  { key: "Filmmaker", icon: Film },
  { key: "Business", icon: Building2 },
  { key: "Entrepreneur", icon: Briefcase },
  { key: "Brand", icon: Palette },
  { key: "Content Creator", icon: Camera },
  { key: "Other", icon: Sparkles },
];

const DECK_SIZES = [
  { pages: 1, price: 2.99, key: "deck_1_page", note: "One-pager" },
  { pages: 5, price: 9.99, key: "deck_5_pages", note: "Mini EPK", featured: true },
  { pages: 10, price: 17.99, key: "deck_10_pages", note: "Full EPK" },
];

const TONES = [
  { key: "keep", label: "Keep My Writing" },
  { key: "improve", label: "Improve" },
  { key: "professional", label: "Professional" },
  { key: "investor", label: "Investor" },
  { key: "label", label: "Record Label" },
  { key: "casting", label: "Casting" },
  { key: "brand", label: "Brand Sponsorship" },
  { key: "luxury", label: "Luxury" },
  { key: "short", label: "Short" },
  { key: "long", label: "Long" },
  { key: "magazine", label: "Magazine" },
];

const DESIGN_KEYWORDS = [
  "Luxury", "Dark", "Minimal", "Streetwear", "Editorial", "Magazine",
  "Fashion", "Corporate", "Sci-Fi", "Retro", "Neon", "Elegant",
  "Bold", "Cinematic", "Premium",
];

const SOCIAL_PLATFORMS = [
  { name: "Instagram", icon: Instagram },
  { name: "TikTok", icon: Music2 },
  { name: "YouTube", icon: Youtube },
  { name: "Spotify", icon: Music },
  { name: "Apple Music", icon: Music },
  { name: "SoundCloud", icon: Music },
  { name: "X", icon: Twitter },
  { name: "Facebook", icon: Facebook },
  { name: "LinkedIn", icon: Linkedin },
  { name: "IMDb", icon: Video },
  { name: "Website", icon: Globe },
];

const STEPS = [
  "Creator Type", "Deck Size", "Information", "Media",
  "AI Enhance", "Design Concepts", "Pages", "Checkout", "Generate",
];

export default function Wizard() {
  const nav = useNavigate();
  const { projectId } = useParams();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [project, setProject] = useState(null);
  const [creatorType, setCreatorType] = useState("");
  const [deckSize, setDeckSize] = useState(5);
  const [orientation, setOrientation] = useState("landscape");
  const [info, setInfo] = useState({
    stage_name: "", real_name: "", headline: "", biography: "",
    career_highlights: "", achievements: "", awards: "", current_projects: "",
    skills: "", genres: "", years_active: "", location: "", representation: "",
    booking_contact: "", email: "", phone: "", website: "",
    socials: [], design_keywords: [],
  });
  const [mainImage, setMainImage] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [concepts, setConcepts] = useState([]);
  const [selectedConceptId, setSelectedConceptId] = useState(null);
  const [pages, setPages] = useState([]);
  const [genStatus, setGenStatus] = useState("draft");
  const [busy, setBusy] = useState(false);
  const [tosOk, setTosOk] = useState(false);

  // Load existing project if projectId param present
  useEffect(() => {
    if (!projectId) return;
    projectsApi.get(projectId).then((p) => {
      setProject(p);
      setCreatorType(p.creator_type || "");
      setDeckSize(p.deck_size || 5);
      setOrientation(p.orientation || "landscape");
      setInfo({ ...info, ...p.info });
      setMainImage(p.main_image || null);
      setGallery(p.media || []);
      setConcepts(p.concepts || []);
      setSelectedConceptId(p.selected_concept_id);
      setPages(p.pages || []);
      // Seed genStatus from the server so a mid-generation refresh doesn't re-trigger generate.
      if (p.status) setGenStatus(p.status);
      if (p.status === "ready" || p.pdf_path) setStep(8);
      else if (p.status === "generating") setStep(8);
      else if (p.paid) setStep(8);  // paid but not yet generated → resume at generate step
    }).catch(() => toast.error("Project not found"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const currentDeckTier = DECK_SIZES.find((t) => deckSize <= t.pages) || DECK_SIZES[DECK_SIZES.length - 1];

  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const saveProject = useCallback(async (extra = {}) => {
    if (!project) return null;
    const payload = {
      creator_type: creatorType,
      deck_size: deckSize,
      orientation,
      info,
      main_image: mainImage,
      media: gallery,
      selected_concept_id: selectedConceptId,
      pages,
      concepts,
      ...extra,
    };
    const p = await projectsApi.update(project.id, payload);
    setProject(p);
    return p;
  }, [project, creatorType, deckSize, orientation, info, mainImage, gallery, selectedConceptId, pages, concepts]);

  const handleCreatorPick = async (key) => {
    setCreatorType(key);
    setBusy(true);
    try {
      const p = await projectsApi.create({ creator_type: key, deck_size: deckSize });
      setProject(p);
      nav(`/create/${p.id}`, { replace: true });
      goNext();
    } catch {
      toast.error("Could not start project");
    } finally {
      setBusy(false);
    }
  };

  const handleGenerateConcepts = async () => {
    setBusy(true);
    try {
      await saveProject();
      const { concepts: c } = await aiApi.concepts(project.id);
      setConcepts(c);
      goNext();
    } catch {
      toast.error("AI concepts failed — please retry");
    } finally {
      setBusy(false);
    }
  };

  const handleSuggestPages = async () => {
    setBusy(true);
    try {
      await saveProject({ selected_concept_id: selectedConceptId });
      const { pages: p } = await aiApi.suggestPages(project.id);
      setPages(p);
      goNext();
    } catch {
      toast.error("Could not generate pages");
    } finally {
      setBusy(false);
    }
  };

  const handleCheckout = async () => {
    if (!tosOk) return toast.error("Please accept the Terms and Content policy");
    setBusy(true);
    try {
      await saveProject();
      const { checkout_url } = await paymentsApi.checkout(currentDeckTier.key, project.id, 1);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error("Could not start checkout");
      setBusy(false);
    }
  };

  const handleGenerate = async () => {
    setBusy(true);
    try {
      await projectsApi.generate(project.id);
      setGenStatus("generating");
      pollStatus();
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error("Payment required — heading to checkout");
        setStep(7);
      } else {
        toast.error("Generation failed to start");
      }
    } finally {
      setBusy(false);
    }
  };

  const pollStatus = useCallback(() => {
    const id = setInterval(async () => {
      try {
        const s = await projectsApi.status(project.id);
        setGenStatus(s.status);
        if (s.status === "ready" || s.status === "failed") clearInterval(id);
      } catch {
        clearInterval(id);
      }
    }, 2000);
  }, [project]);

  const handleDownload = () => {
    if (!project) return;
    window.open(projectsApi.pdfUrl(project.id), "_blank");
  };

  return (
    <div className="min-h-screen text-white relative">
      <Nav />
      <div className="pt-10 pb-16 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <ProgressBar step={step} steps={STEPS} />
          <div className={`mt-8 grid gap-6 ${step >= 2 && step <= 6 ? "lg:grid-cols-[1fr_360px]" : "grid-cols-1"}`}>
            <div className="kd-panel p-6 md:p-10 relative overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  {step === 0 && (
                    <StepCreatorType selected={creatorType} onPick={handleCreatorPick} busy={busy} />
                  )}
                  {step === 1 && (
                    <StepDeckSize
                      deckSize={deckSize} onChange={setDeckSize}
                      orientation={orientation} onOrientationChange={setOrientation}
                      onNext={async () => { await saveProject({ deck_size: deckSize, orientation }); goNext(); }}
                    />
                  )}
                  {step === 2 && (
                    <StepInfo info={info} setInfo={setInfo} onNext={async () => { await saveProject(); goNext(); }} onBack={goBack} />
                  )}
                  {step === 3 && (
                    <StepMedia mainImage={mainImage} setMainImage={setMainImage} gallery={gallery} setGallery={setGallery} onNext={async () => { await saveProject(); goNext(); }} onBack={goBack} />
                  )}
                  {step === 4 && (
                    <StepEnhance info={info} setInfo={setInfo} creatorType={creatorType} onNext={async () => { await saveProject(); handleGenerateConcepts(); }} onBack={goBack} busy={busy} />
                  )}
                  {step === 5 && (
                    <StepConcepts
                      concepts={concepts}
                      selectedId={selectedConceptId}
                      onSelect={setSelectedConceptId}
                      onNext={handleSuggestPages}
                      onBack={goBack}
                      busy={busy}
                      projectId={project?.id}
                      info={info}
                      mainImage={mainImage}
                      onTemplateApplied={(newConcepts, selectedId) => {
                        setConcepts(newConcepts);
                        setSelectedConceptId(selectedId);
                      }}
                      onConceptsChange={(next) => setConcepts(next)}
                    />
                  )}
                  {step === 6 && (
                    <StepPages pages={pages} setPages={setPages} onNext={async () => { await saveProject(); goNext(); }} onBack={goBack} />
                  )}
                  {step === 7 && (
                    <StepCheckout tier={currentDeckTier} deckSize={deckSize} tosOk={tosOk} setTosOk={setTosOk} onCheckout={handleCheckout} onBack={goBack} paid={project?.paid} onSkip={() => { setStep(8); handleGenerate(); }} busy={busy} />
                  )}
                  {step === 8 && (
                    <StepGenerate genStatus={genStatus} onGenerate={handleGenerate} onDownload={handleDownload} busy={busy} project={project} user={user} onSignUp={() => nav("/auth?claim=" + project?.id)} />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
            {step >= 2 && step <= 6 && (
              <div className="hidden lg:block">
                <LivePreview
                  info={info}
                  mainImage={mainImage}
                  gallery={gallery}
                  concept={concepts.find((c) => c.id === selectedConceptId) || concepts[0]}
                  pages={pages}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ Progress Bar ============
function ProgressBar({ step, steps }) {
  return (
    <div className="flex flex-wrap items-center gap-2 md:gap-3">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center gap-2 md:gap-3">
          <div
            data-testid={`step-${i}`}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              i < step ? "bg-[#E50914] w-8" : i === step ? "bg-white w-14" : "bg-white/10 w-8"
            }`}
          />
          <span className={`hidden lg:inline text-[10px] tracking-[0.2em] uppercase transition-colors ${i === step ? "text-white" : "text-zinc-600"}`}>
            {s}
          </span>
        </div>
      ))}
    </div>
  );
}

// ============ STEP 0 · Creator Type ============
function StepCreatorType({ selected, onPick, busy }) {
  return (
    <div>
      <div className="kd-tag mb-4">Step 1 · Creator type</div>
      <h1 className="kd-display text-3xl md:text-5xl tracking-tight mb-3">Who are we building this for?</h1>
      <p className="text-zinc-400 max-w-xl mb-10">Pick your creator category. This shapes the writing style, page order and visual direction.</p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {CREATOR_TYPES.map((c, i) => {
          const Icon = c.icon;
          const active = selected === c.key;
          return (
            <motion.button
              key={c.key}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              disabled={busy}
              onClick={() => onPick(c.key)}
              data-testid={`creator-${c.key.replace(/\s+/g, "-").toLowerCase()}`}
              className={`text-left rounded-2xl p-5 border transition-all duration-300 disabled:opacity-50 ${
                active
                  ? "bg-[#E50914]/10 border-[#E50914]/60 kd-red-glow"
                  : "bg-white/[0.02] border-white/[0.06] hover:border-white/20 hover:bg-white/[0.05]"
              }`}
            >
              <Icon size={18} className={active ? "text-[#FF2A2D]" : "text-zinc-400"} />
              <div className="mt-3 text-sm text-white font-medium">{c.key}</div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

// ============ STEP 1 · Deck Size ============
function StepDeckSize({ deckSize, onChange, orientation, onOrientationChange, onNext }) {
  return (
    <div>
      <div className="kd-tag mb-4">Step 2 · Deck size &amp; format</div>
      <h1 className="kd-display text-3xl md:text-5xl tracking-tight mb-3">How long should your deck be?</h1>
      <p className="text-zinc-400 max-w-xl mb-10">Pick a base tier. You can add more pages later.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {DECK_SIZES.map((t) => {
          const active = deckSize === t.pages;
          return (
            <button
              key={t.pages}
              onClick={() => onChange(t.pages)}
              data-testid={`decksize-${t.pages}`}
              className={`text-left rounded-3xl p-8 border transition-all duration-300 ${
                active
                  ? "bg-[#E50914]/10 border-[#E50914]/60 kd-red-glow"
                  : "bg-white/[0.02] border-white/[0.06] hover:border-white/20"
              }`}
            >
              <div className="text-zinc-500 text-[11px] tracking-[0.2em] uppercase mb-2">{t.note}</div>
              <div className="kd-display text-5xl mb-2">{t.pages}</div>
              <div className="text-zinc-400 text-sm">pages</div>
              <div className="mt-6 text-2xl kd-display">${t.price}</div>
            </button>
          );
        })}
      </div>
      <div className="mt-6 rounded-2xl p-5 bg-white/[0.02] border border-white/[0.06] flex items-center gap-4 flex-wrap">
        <span className="text-zinc-400 text-sm">Need more?</span>
        <input
          type="number" min={1} max={30}
          value={deckSize}
          onChange={(e) => onChange(Math.max(1, Math.min(30, parseInt(e.target.value) || 1)))}
          data-testid="decksize-custom"
          className="bg-black/40 border border-white/10 rounded-xl px-4 py-2 w-28 text-white outline-none focus:border-white/30"
        />
        <span className="text-zinc-500 text-xs">pages · Additional pages billed at $1.99 each above 10.</span>
      </div>

      {/* Orientation */}
      <div className="mt-6">
        <div className="text-zinc-500 text-[11px] tracking-[0.2em] uppercase mb-3">Page orientation</div>
        <div className="grid grid-cols-2 gap-4">
          {[
            { key: "landscape", label: "Landscape", note: "Presentations, screens, EPKs", w: 60, h: 40 },
            { key: "portrait", label: "Portrait", note: "Print, media kits, brochures", w: 40, h: 60 },
          ].map((o) => {
            const active = orientation === o.key;
            return (
              <button
                key={o.key}
                onClick={() => onOrientationChange(o.key)}
                data-testid={`orientation-${o.key}`}
                className={`p-5 rounded-2xl border transition-all duration-300 flex items-center gap-5 ${
                  active
                    ? "bg-[#E50914]/10 border-[#E50914]/60 kd-red-glow"
                    : "bg-white/[0.02] border-white/[0.06] hover:border-white/20"
                }`}
              >
                <div
                  className="flex-shrink-0 rounded border border-white/25"
                  style={{ width: o.w, height: o.h, background: "linear-gradient(180deg, #171717 0%, #050505 100%)" }}
                />
                <div className="text-left">
                  <div className="text-white font-medium">{o.label}</div>
                  <div className="text-zinc-500 text-xs mt-1">{o.note}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-10 flex justify-end">
        <PrimaryBtn onClick={onNext} testId="decksize-next">Continue <ArrowRight size={16} /></PrimaryBtn>
      </div>
    </div>
  );
}

// ============ STEP 2 · Info ============
function StepInfo({ info, setInfo, onNext, onBack }) {
  const setF = (k) => (e) => setInfo({ ...info, [k]: e.target.value });
  const addSocial = () => setInfo({ ...info, socials: [...info.socials, { platform: "Instagram", url: "", followers: "" }] });
  const rmSocial = (i) => setInfo({ ...info, socials: info.socials.filter((_, j) => j !== i) });
  const setSocial = (i, k, v) => setInfo({ ...info, socials: info.socials.map((s, j) => j === i ? { ...s, [k]: v } : s) });

  const toggleKeyword = (kw) => {
    const has = info.design_keywords.includes(kw);
    setInfo({ ...info, design_keywords: has ? info.design_keywords.filter((k) => k !== kw) : [...info.design_keywords, kw] });
  };

  return (
    <div>
      <div className="kd-tag mb-4">Step 3 · Information</div>
      <h1 className="kd-display text-3xl md:text-5xl tracking-tight mb-3">Tell us your story.</h1>
      <p className="text-zinc-400 max-w-xl mb-10">The more you share, the sharper your deck.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          ["stage_name", "Stage / Brand Name *"],
          ["real_name", "Real Name"],
          ["headline", "Professional Headline"],
          ["location", "Location"],
          ["genres", "Genres / Categories"],
          ["years_active", "Years Active"],
          ["representation", "Representation / Manager"],
          ["booking_contact", "Booking Contact"],
          ["email", "Email"],
          ["phone", "Phone"],
          ["website", "Website"],
        ].map(([k, label]) => (
          <Field key={k} label={label}>
            <input value={info[k] || ""} onChange={setF(k)} data-testid={`info-${k}`} className={inputCls} />
          </Field>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          ["biography", "Biography", 5],
          ["career_highlights", "Career Highlights", 4],
          ["achievements", "Achievements", 3],
          ["awards", "Awards", 3],
          ["current_projects", "Current Projects", 3],
          ["skills", "Skills / Services", 3],
        ].map(([k, label, rows]) => (
          <Field key={k} label={label}>
            <textarea rows={rows} value={info[k] || ""} onChange={setF(k)} data-testid={`info-${k}`} className={inputCls} />
          </Field>
        ))}
      </div>

      {/* Socials */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="kd-display text-2xl tracking-tight">Social & Links</h3>
          <button onClick={addSocial} data-testid="add-social" className="rounded-full px-4 py-2 text-xs bg-white/5 hover:bg-white/10 border border-white/10 transition-colors flex items-center gap-2">
            <Plus size={12} /> Add link
          </button>
        </div>
        <div className="space-y-3">
          {info.socials.map((s, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <select value={s.platform} onChange={(e) => setSocial(i, "platform", e.target.value)} data-testid={`social-platform-${i}`} className={`${inputCls} col-span-3`}>
                {SOCIAL_PLATFORMS.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
                <option value="Other">Other</option>
              </select>
              <input value={s.url} onChange={(e) => setSocial(i, "url", e.target.value)} placeholder="https://…" data-testid={`social-url-${i}`} className={`${inputCls} col-span-6`} />
              <input value={s.followers} onChange={(e) => setSocial(i, "followers", e.target.value)} placeholder="Followers (opt.)" data-testid={`social-followers-${i}`} className={`${inputCls} col-span-2`} />
              <button onClick={() => rmSocial(i)} data-testid={`social-remove-${i}`} className="col-span-1 rounded-xl p-2 bg-white/5 hover:bg-white/10 border border-white/10">
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Design keywords */}
      <div className="mt-10">
        <h3 className="kd-display text-2xl tracking-tight mb-4">Design keywords <span className="text-zinc-500 text-sm font-sans">(optional)</span></h3>
        <div className="flex flex-wrap gap-2">
          {DESIGN_KEYWORDS.map((kw) => {
            const on = info.design_keywords.includes(kw);
            return (
              <button key={kw} onClick={() => toggleKeyword(kw)} data-testid={`kw-${kw.toLowerCase()}`} className={`rounded-full px-4 py-2 text-xs transition-all ${on ? "bg-[#E50914] text-white border border-[#E50914]" : "bg-white/[0.03] border border-white/[0.08] text-zinc-300 hover:bg-white/[0.06]"}`}>
                {kw}
              </button>
            );
          })}
        </div>
      </div>

      <NavBtns onBack={onBack} onNext={onNext} disabled={!info.stage_name} nextTest="info-next" />
    </div>
  );
}

const inputCls = "w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-white/30 focus:ring-1 focus:ring-white/20 transition-colors placeholder:text-zinc-600";

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-zinc-500 text-[11px] tracking-[0.2em] uppercase mb-2">{label}</div>
      {children}
    </label>
  );
}

// ============ STEP 3 · Media ============
function StepMedia({ mainImage, setMainImage, gallery, setGallery, onNext, onBack }) {
  const [uploading, setUploading] = useState(false);

  const handleMain = async (files) => {
    if (!files || !files[0]) return;
    setUploading(true);
    try {
      const { images } = await uploadApi.images([files[0]]);
      setMainImage(images[0]);
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const handleGallery = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      const { images } = await uploadApi.images(Array.from(files));
      setGallery([...gallery, ...images]);
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  return (
    <div>
      <div className="kd-tag mb-4">Step 4 · Media</div>
      <h1 className="kd-display text-3xl md:text-5xl tracking-tight mb-3">Bring the visuals.</h1>
      <p className="text-zinc-400 max-w-xl mb-10">Photos, logos, artwork. We auto-optimize and crop intelligently.</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1">
          <div className="text-zinc-500 text-[11px] tracking-[0.2em] uppercase mb-3">Main character image *</div>
          <Dropzone accept="image/*" onFiles={handleMain} multiple={false} testId="upload-main">
            {mainImage ? (
              <div className="relative">
                <img src={mainImage} alt="main" className="w-full aspect-[3/4] object-cover rounded-2xl" />
                <button onClick={() => setMainImage(null)} className="absolute top-3 right-3 rounded-full p-2 bg-black/60 border border-white/10 hover:bg-black/80">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="aspect-[3/4] flex flex-col items-center justify-center text-zinc-400 gap-3">
                <Upload size={28} />
                <div className="text-sm">Drop or click to upload</div>
                <div className="text-xs text-zinc-600">JPG · PNG · WebP</div>
              </div>
            )}
          </Dropzone>
        </div>
        <div className="md:col-span-2">
          <div className="text-zinc-500 text-[11px] tracking-[0.2em] uppercase mb-3">Gallery, logos, artwork</div>
          <Dropzone accept="image/*" onFiles={handleGallery} multiple testId="upload-gallery">
            <div className="min-h-[220px] flex flex-col items-center justify-center text-zinc-400 gap-3 py-6">
              <Upload size={26} />
              <div className="text-sm">Add multiple images at once</div>
              <div className="text-xs text-zinc-600">We&apos;ll compress & crop them for print quality</div>
            </div>
          </Dropzone>
          {gallery.length > 0 && (
            <div className="mt-4 grid grid-cols-3 md:grid-cols-5 gap-3">
              {gallery.map((g, i) => (
                <div key={i} className="relative group">
                  <img src={g} alt={`g${i}`} className="w-full aspect-square object-cover rounded-xl" />
                  <button onClick={() => setGallery(gallery.filter((_, j) => j !== i))} data-testid={`remove-gallery-${i}`} className="absolute top-2 right-2 rounded-full p-1.5 bg-black/70 border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity">
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {uploading && <div className="mt-6 text-zinc-400 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Optimizing…</div>}

      <NavBtns onBack={onBack} onNext={onNext} disabled={!mainImage} nextTest="media-next" />
    </div>
  );
}

function Dropzone({ children, onFiles, accept, multiple, testId }) {
  const [drag, setDrag] = useState(false);
  return (
    <label
      data-testid={testId}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); onFiles(e.dataTransfer.files); }}
      className={`block rounded-2xl border-2 border-dashed transition-all cursor-pointer ${drag ? "border-[#E50914]/60 bg-[#E50914]/5" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}
    >
      <input type="file" accept={accept} multiple={multiple} onChange={(e) => onFiles(e.target.files)} className="hidden" />
      <div className="p-4">{children}</div>
    </label>
  );
}

// ============ STEP 4 · AI Enhance ============
function StepEnhance({ info, setInfo, creatorType, onNext, onBack, busy }) {
  const [target, setTarget] = useState("biography");
  const [applying, setApplying] = useState(false);

  const apply = async (tone) => {
    if (!info[target]) { toast.error("This field is empty"); return; }
    setApplying(true);
    try {
      const { text } = await aiApi.enhance(info[target], tone, creatorType);
      setInfo({ ...info, [target]: text });
      toast.success("Rewritten");
    } catch { toast.error("AI rewrite failed"); } finally { setApplying(false); }
  };

  return (
    <div>
      <div className="kd-tag mb-4">Step 5 · AI Enhance</div>
      <h1 className="kd-display text-3xl md:text-5xl tracking-tight mb-3">Sharpen your words. <span className="kd-serif kd-chrome-text">Optional.</span></h1>
      <p className="text-zinc-400 max-w-xl mb-10">Pick a section and a tone. AI rewrites it while preserving your facts.</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <div className="text-zinc-500 text-[11px] tracking-[0.2em] uppercase mb-3">Target section</div>
          <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="enhance-target" className={inputCls}>
            <option value="biography">Biography</option>
            <option value="career_highlights">Career Highlights</option>
            <option value="achievements">Achievements</option>
            <option value="skills">Skills / Services</option>
            <option value="current_projects">Current Projects</option>
            <option value="headline">Headline</option>
          </select>
          <div className="mt-6 text-zinc-500 text-[11px] tracking-[0.2em] uppercase mb-3">Tone</div>
          <div className="flex flex-wrap gap-2">
            {TONES.map((t) => (
              <button
                key={t.key}
                onClick={() => apply(t.key)}
                disabled={applying}
                data-testid={`tone-${t.key}`}
                className="rounded-full px-3 py-1.5 text-xs bg-white/[0.03] hover:bg-[#E50914]/15 border border-white/[0.08] hover:border-[#E50914]/40 text-zinc-200 transition-all disabled:opacity-50"
              >
                {applying ? <Loader2 size={10} className="animate-spin inline" /> : <Wand2 size={10} className="inline mr-1.5" />} {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="text-zinc-500 text-[11px] tracking-[0.2em] uppercase mb-3">Preview / edit</div>
          <textarea
            rows={16}
            value={info[target] || ""}
            onChange={(e) => setInfo({ ...info, [target]: e.target.value })}
            data-testid="enhance-preview"
            className={inputCls}
          />
        </div>
      </div>

      <NavBtns onBack={onBack} onNext={onNext} nextTest="enhance-next" nextLabel={busy ? "Generating concepts…" : "Generate 3 concepts"} disabled={busy} />
    </div>
  );
}

// ============ STEP 5 · Concepts ============
function ConceptCoverPreview({ palette = [], typography, name, tagline, stageName, headline, mainImage }) {
  const bg = palette[0] || "#0a0a0a";
  const accent = palette[1] || "#E50914";
  const text = palette[2] || "#FFFFFF";
  const support = palette[3] || "#71717a";
  const displayName = (stageName || "Your Name").slice(0, 22);
  const displayHeadline = (headline || tagline || name || "Cinematic pitch deck").slice(0, 90);
  const headingFont = typography?.heading || "Clash Display";

  return (
    <motion.div layout className="aspect-[4/3] relative overflow-hidden" style={{ background: bg }}>
      <motion.div
        key={`bloom-${accent}`}
        initial={{ opacity: 0.6 }}
        animate={{ opacity: 0.9 }}
        transition={{ duration: 0.35 }}
        className="absolute inset-0"
        style={{ background: `radial-gradient(circle at 82% 18%, ${accent}55, transparent 55%)` }}
      />
      {mainImage && (
        <img
          src={mainImage}
          alt=""
          className="absolute right-0 top-0 h-full w-1/2 object-cover"
          style={{ maskImage: "linear-gradient(90deg, transparent 0%, black 35%)" }}
        />
      )}
      <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.35) 100%)" }} />
      <div className="absolute inset-0 p-4 flex flex-col justify-between">
        <div className="flex items-center justify-between">
          <div className="text-[8px] tracking-[0.28em] uppercase" style={{ color: text + "aa" }}>Electronic Press Kit</div>
          <motion.div
            key={`dot-${accent}`}
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: accent, boxShadow: `0 0 8px ${accent}` }}
          />
        </div>
        <div>
          <div
            className="leading-[0.9] tracking-tight"
            style={{
              fontFamily: `"${headingFont}", "Clash Display", sans-serif`,
              fontSize: "clamp(20px, 2.6vw, 30px)",
              color: text,
            }}
          >
            {displayName}
          </div>
          <div className="mt-2 max-w-[85%] text-[10px] leading-snug" style={{ color: text + "b3" }}>
            {displayHeadline}
          </div>
          <motion.div
            key={`bar-${accent}`}
            initial={{ scaleX: 0, transformOrigin: "left" }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="mt-2 h-[2px] w-8"
            style={{ background: accent }}
          />
          <div className="mt-1 text-[8px] tracking-[0.22em] uppercase" style={{ color: support + "cc" }}>{name}</div>
        </div>
      </div>
    </motion.div>
  );
}

function StepConcepts({ concepts, selectedId, onSelect, onNext, onBack, busy, projectId, onTemplateApplied, onConceptsChange, info, mainImage }) {
  const [mode, setMode] = useState("ai"); // "ai" | "templates"
  const [tpls, setTpls] = useState([]);
  const [applying, setApplying] = useState(false);

  const [originalPalettes, setOriginalPalettes] = useState({});

  useEffect(() => {
    // Snapshot each concept's original palette the first time we see it.
    setOriginalPalettes((prev) => {
      const next = { ...prev };
      let changed = false;
      concepts.forEach((c) => {
        if (c.id && !next[c.id] && c.palette) {
          next[c.id] = [...c.palette];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [concepts]);

  useEffect(() => {
    templatesApi.list().then((r) => setTpls(r.templates || [])).catch(() => {});
  }, []);

  const applyTemplate = async (slug) => {
    setApplying(true);
    try {
      const { concept, concepts: allConcepts } = await templatesApi.apply(projectId, slug);
      onTemplateApplied?.(allConcepts, concept.id);
      toast.success(`${concept.name} applied`);
    } catch { toast.error("Could not apply template"); } finally { setApplying(false); }
  };

  const updatePaletteColor = (conceptId, colorIdx, newColor) => {
    const next = concepts.map((c) => {
      if (c.id !== conceptId) return c;
      const palette = [...(c.palette || [])];
      palette[colorIdx] = newColor;
      return { ...c, palette };
    });
    onConceptsChange?.(next);
  };

  const selectedConcept = concepts.find((c) => c.id === selectedId);

  return (
    <div>
      <div className="kd-tag mb-4">Step 6 · Design</div>
      <h1 className="kd-display text-3xl md:text-5xl tracking-tight mb-3">Pick a direction.</h1>
      <p className="text-zinc-400 max-w-xl mb-8">Choose an AI-generated concept, or start from a curated cinematic template. Fine-tune the palette below.</p>

      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setMode("ai")}
          data-testid="concepts-mode-ai"
          className={`rounded-full px-5 py-2 text-[11px] tracking-[0.2em] uppercase transition-all ${mode === "ai" ? "bg-[#E50914] text-white" : "bg-white/[0.03] border border-white/[0.08] text-zinc-300"}`}
        >
          AI concepts
        </button>
        <button
          onClick={() => setMode("templates")}
          data-testid="concepts-mode-templates"
          className={`rounded-full px-5 py-2 text-[11px] tracking-[0.2em] uppercase transition-all ${mode === "templates" ? "bg-[#E50914] text-white" : "bg-white/[0.03] border border-white/[0.08] text-zinc-300"}`}
        >
          Curated templates
        </button>
      </div>

      {mode === "ai" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {concepts.filter((c) => !c.slug).map((c, i) => {
            const active = selectedId === c.id;
            return (
              <motion.button
                key={c.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                onClick={() => onSelect(c.id)}
                data-testid={`concept-${i}`}
                className={`text-left rounded-3xl overflow-hidden border-2 transition-all duration-300 ${active ? "border-[#E50914] kd-red-glow" : "border-white/[0.06] hover:border-white/25"}`}
              >
                <ConceptCoverPreview
                  palette={c.palette}
                  typography={c.typography}
                  name={c.name}
                  tagline={c.tagline}
                  stageName={info?.stage_name}
                  headline={info?.headline}
                  mainImage={mainImage}
                />
                <div className="p-5 bg-black/50">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm text-zinc-300 italic kd-serif truncate">&ldquo;{c.tagline}&rdquo;</p>
                    {active && (
                      <div className="flex items-center gap-1 text-[#FF2A2D] text-[10px] flex-shrink-0">
                        <Check size={12} /> Selected
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1.5 mb-3">
                    {(c.palette || []).map((p, idx) => (
                      <motion.div
                        key={idx}
                        layout
                        animate={{ background: p }}
                        transition={{ duration: 0.25 }}
                        className="w-6 h-6 rounded-full border border-white/10"
                        style={{ background: p }}
                      />
                    ))}
                  </div>
                  <div className="text-xs text-zinc-500 leading-relaxed">{c.graphic_style}</div>
                </div>
              </motion.button>
            );
          })}
        </div>
      )}

      {mode === "templates" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {tpls.map((t, i) => {
            // If this template was applied, use the (possibly edited) palette + concept ID from state.
            const applied = concepts.find((c) => c.slug === t.slug);
            const effectivePalette = applied?.palette || t.palette;
            const effectiveTypography = applied?.typography || t.typography;
            const isPicked = applied?.id === selectedId;
            return (
              <motion.button
                key={t.slug}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                onClick={() => applyTemplate(t.slug)}
                disabled={applying}
                data-testid={`template-${t.slug}`}
                className={`text-left rounded-3xl overflow-hidden border-2 transition-all duration-300 disabled:opacity-60 ${isPicked ? "border-[#E50914] kd-red-glow" : "border-white/[0.06] hover:border-white/25"}`}
              >
                <ConceptCoverPreview
                  palette={effectivePalette}
                  typography={effectiveTypography}
                  name={t.name}
                  tagline={t.tagline}
                  stageName={info?.stage_name}
                  headline={info?.headline}
                  mainImage={mainImage}
                />
                <div className="p-5 bg-black/50">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs text-zinc-400 italic kd-serif truncate">&ldquo;{t.tagline}&rdquo;</p>
                    {isPicked && (
                      <div className="flex items-center gap-1 text-[#FF2A2D] text-[10px] flex-shrink-0">
                        <Check size={12} /> Applied
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1.5 mb-3">
                    {(effectivePalette || []).map((p, idx) => (
                      <motion.div
                        key={idx}
                        layout
                        animate={{ background: p }}
                        transition={{ duration: 0.25 }}
                        className="w-5 h-5 rounded-full border border-white/10"
                        style={{ background: p }}
                      />
                    ))}
                  </div>
                  <div className="text-[11px] text-zinc-500 leading-relaxed">{t.vibe}</div>
                </div>
              </motion.button>
            );
          })}
        </div>
      )}

      {/* Palette editor for the currently selected concept */}
      {selectedConcept && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 rounded-3xl p-6 md:p-8 bg-white/[0.02] border border-white/[0.06]"
        >
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
            <div>
              <div className="kd-tag mb-2 !bg-[#E50914]/15 !border-[#E50914]/40 !text-[#FF2A2D]">Customize palette</div>
              <h3 className="kd-display text-xl">Make {selectedConcept.name} yours.</h3>
              <p className="text-zinc-500 text-xs mt-1">Click any swatch to pick a new colour. Changes apply to every page.</p>
            </div>
            <button
              onClick={() => {
                const original = originalPalettes[selectedConcept.id];
                if (!original) return;
                onConceptsChange?.(concepts.map((c) => c.id === selectedConcept.id ? { ...c, palette: [...original] } : c));
              }}
              data-testid="palette-reset"
              className="rounded-full px-4 py-2 text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300"
            >
              Reset
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {(selectedConcept.palette || []).map((color, i) => (
              <div key={i} className="rounded-2xl p-3 bg-black/30 border border-white/[0.06]">
                <div className="text-zinc-500 text-[10px] tracking-[0.25em] uppercase mb-2">
                  {["Background", "Accent", "Highlight", "Support"][i] || `Colour ${i + 1}`}
                </div>
                <label className="relative block cursor-pointer group">
                  <div
                    className="w-full aspect-square rounded-xl border border-white/10 group-hover:border-white/30 transition-colors"
                    style={{ background: color }}
                  />
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => updatePaletteColor(selectedConcept.id, i, e.target.value)}
                    data-testid={`palette-swatch-${i}`}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                </label>
                <input
                  type="text"
                  value={color}
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    if (/^#[0-9a-fA-F]{6}$/.test(v)) updatePaletteColor(selectedConcept.id, i, v);
                  }}
                  data-testid={`palette-hex-${i}`}
                  className="mt-2 w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs font-mono outline-none focus:border-white/30 uppercase"
                />
              </div>
            ))}
          </div>
        </motion.div>
      )}

      <NavBtns onBack={onBack} onNext={onNext} disabled={!selectedId || busy} nextTest="concepts-next" nextLabel={busy ? "Preparing pages…" : "Continue"} />
    </div>
  );
}

// ============ STEP 6 · Pages ============
function StepPages({ pages, setPages, onNext, onBack }) {
  const [drag, setDrag] = useState(null);

  const rename = (i, v) => setPages(pages.map((p, j) => j === i ? { ...p, title: v } : p));
  const remove = (i) => setPages(pages.filter((_, j) => j !== i));
  const add = () => setPages([...pages, { id: `p${Date.now()}`, title: "New Page", module: "content", content: "" }]);
  const move = (from, to) => {
    if (from === to || to < 0 || to >= pages.length) return;
    const next = [...pages];
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    setPages(next);
  };

  return (
    <div>
      <div className="kd-tag mb-4">Step 7 · Pages</div>
      <h1 className="kd-display text-3xl md:text-5xl tracking-tight mb-3">Sequence your deck.</h1>
      <p className="text-zinc-400 max-w-xl mb-10">Drag to reorder. Rename freely. Remove or add pages.</p>

      <div className="space-y-2">
        {pages.map((p, i) => (
          <div
            key={p.id}
            draggable
            onDragStart={() => setDrag(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => { move(drag, i); setDrag(null); }}
            data-testid={`page-row-${i}`}
            className="grid grid-cols-12 gap-3 items-center rounded-2xl bg-white/[0.02] border border-white/[0.06] p-4 hover:border-white/15 transition-colors"
          >
            <div className="col-span-1 flex items-center gap-2 text-zinc-500">
              <GripVertical size={16} className="cursor-move" />
              <span className="text-xs">{String(i + 1).padStart(2, "0")}</span>
            </div>
            <input
              value={p.title}
              onChange={(e) => rename(i, e.target.value)}
              data-testid={`page-title-${i}`}
              className="col-span-7 bg-transparent border-b border-transparent hover:border-white/10 focus:border-white/30 outline-none text-white py-1"
            />
            <div className="col-span-3 text-zinc-500 text-xs tracking-[0.2em] uppercase">{p.module}</div>
            <button onClick={() => remove(i)} data-testid={`page-remove-${i}`} className="col-span-1 rounded-xl p-2 bg-white/[0.03] hover:bg-white/[0.08] border border-white/[0.08]">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
      <button onClick={add} data-testid="page-add" className="mt-4 rounded-full px-4 py-2 text-xs bg-white/5 hover:bg-white/10 border border-white/10 flex items-center gap-2">
        <Plus size={12} /> Add page
      </button>

      <NavBtns onBack={onBack} onNext={onNext} nextTest="pages-next" nextLabel="Continue to checkout" />
    </div>
  );
}

// ============ STEP 7 · Checkout ============
function StepCheckout({ tier, deckSize, tosOk, setTosOk, onCheckout, onBack, paid, onSkip, busy }) {
  const extra = Math.max(0, deckSize - 10);
  const subtotal = tier.price + extra * 1.99;

  return (
    <div>
      <div className="kd-tag mb-4">Step 8 · Checkout</div>
      <h1 className="kd-display text-3xl md:text-5xl tracking-tight mb-3">One click from magic.</h1>
      <p className="text-zinc-400 max-w-xl mb-10">Pay securely with Stripe. Your deck generates instantly after.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-3xl p-8 kd-glass-strong">
          <div className="text-zinc-500 text-[11px] tracking-[0.2em] uppercase mb-3">Order summary</div>
          <div className="flex items-baseline justify-between py-3 border-b border-white/[0.08]">
            <span className="text-white/90">{tier.pages}-page base deck</span>
            <span className="kd-display text-2xl">${tier.price}</span>
          </div>
          {extra > 0 && (
            <div className="flex items-baseline justify-between py-3 border-b border-white/[0.08]">
              <span className="text-white/90">{extra} additional page{extra > 1 ? "s" : ""}</span>
              <span className="text-white">${(extra * 1.99).toFixed(2)}</span>
            </div>
          )}
          <div className="flex items-baseline justify-between py-3 mt-3">
            <span className="text-zinc-400 text-sm">Total</span>
            <span className="kd-display text-4xl kd-chrome-text">${subtotal.toFixed(2)}</span>
          </div>
          <div className="mt-6 flex items-start gap-3">
            <input type="checkbox" checked={tosOk} onChange={(e) => setTosOk(e.target.checked)} data-testid="tos-checkbox" className="mt-1 accent-[#E50914]" />
            <div className="text-xs text-zinc-400 leading-relaxed">
              I confirm I own or have rights to all uploaded media, and I accept the KlickDeck Terms of Service and Privacy Policy.
            </div>
          </div>
          <button
            onClick={onCheckout}
            data-testid="checkout-pay"
            disabled={!tosOk || busy || paid}
            className="mt-6 w-full rounded-full px-6 py-4 bg-[#E50914] hover:bg-[#FF2A2D] text-white font-medium transition-all duration-300 hover:shadow-[0_0_36px_rgba(229,9,20,0.5)] disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : paid ? "Already paid" : "Pay & generate"} <ArrowRight size={16} />
          </button>
          {paid && (
            <button onClick={onSkip} data-testid="checkout-skip" className="mt-3 w-full rounded-full px-6 py-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white">
              Continue to generation
            </button>
          )}
          <div className="mt-4 text-[11px] text-zinc-500 flex items-center justify-center gap-2">
            <span>Secured by Stripe</span>·<span>4242 4242 4242 4242 for test</span>
          </div>
        </div>
        <div className="rounded-3xl p-8 bg-white/[0.02] border border-white/[0.06]">
          <div className="text-zinc-500 text-[11px] tracking-[0.2em] uppercase mb-3">What happens next</div>
          <ol className="space-y-4 text-sm text-zinc-300">
            <li className="flex gap-3"><span className="rounded-full w-6 h-6 flex items-center justify-center bg-[#E50914]/15 border border-[#E50914]/40 text-[#FF2A2D] text-xs">1</span> Stripe checkout in a new page</li>
            <li className="flex gap-3"><span className="rounded-full w-6 h-6 flex items-center justify-center bg-white/10 border border-white/15 text-white text-xs">2</span> AI writes each page in your chosen tone</li>
            <li className="flex gap-3"><span className="rounded-full w-6 h-6 flex items-center justify-center bg-white/10 border border-white/15 text-white text-xs">3</span> Layouts assembled around your uploaded photos</li>
            <li className="flex gap-3"><span className="rounded-full w-6 h-6 flex items-center justify-center bg-white/10 border border-white/15 text-white text-xs">4</span> Download your premium PDF instantly</li>
          </ol>
        </div>
      </div>

      <div className="mt-8 flex justify-between">
        <SecondaryBtn onClick={onBack} testId="checkout-back"><ArrowLeft size={14} /> Back</SecondaryBtn>
      </div>
    </div>
  );
}

// ============ STEP 8 · Generate & Download ============
function StepGenerate({ genStatus, onGenerate, onDownload, busy, project, user, onSignUp }) {
  const messages = [
    "Designing layout…",
    "Generating graphics…",
    "Enhancing images…",
    "Building pages…",
    "Finalizing PDF…",
  ];
  const [msgIdx, setMsgIdx] = useState(0);
  useEffect(() => {
    if (genStatus !== "generating") return;
    const t = setInterval(() => setMsgIdx((i) => (i + 1) % messages.length), 2200);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genStatus]);

  useEffect(() => {
    if (genStatus === "draft" && !busy && project?.paid) onGenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  if (genStatus === "ready") {
    return (
      <div className="text-center py-10">
        <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="mx-auto w-20 h-20 rounded-full bg-[#E50914]/15 border border-[#E50914]/40 flex items-center justify-center mb-6">
          <Check size={32} className="text-[#FF2A2D]" />
        </motion.div>
        <h1 className="kd-display text-4xl md:text-6xl tracking-tight mb-3">Your deck is ready.</h1>
        <p className="text-zinc-400 max-w-md mx-auto mb-10">Download the PDF. Share it with confidence.</p>
        <button onClick={onDownload} data-testid="download-btn" className="rounded-full px-8 py-4 bg-[#E50914] hover:bg-[#FF2A2D] text-white font-medium transition-all duration-300 hover:shadow-[0_0_36px_rgba(229,9,20,0.55)] inline-flex items-center gap-3">
          <Download size={18} /> Download PDF
        </button>
        {!user && (
          <div className="mt-12 kd-glass rounded-3xl p-8 max-w-xl mx-auto">
            <div className="kd-tag mb-3">Save this project</div>
            <h3 className="kd-display text-2xl mb-3">Keep it in your dashboard.</h3>
            <p className="text-zinc-400 text-sm mb-5">Sign up with email + phone to save this deck and unlock revisions later.</p>
            <button onClick={onSignUp} data-testid="signup-claim" className="rounded-full px-6 py-3 bg-white/10 hover:bg-white/15 border border-white/10 text-white text-sm">
              Create account & save
            </button>
          </div>
        )}
      </div>
    );
  }

  if (genStatus === "failed") {
    return (
      <div className="text-center py-16">
        <h2 className="kd-display text-3xl">Something went sideways.</h2>
        <p className="text-zinc-400 mt-3 mb-6">Try again — we&apos;ll pick up right where we left off.</p>
        <PrimaryBtn onClick={onGenerate} testId="retry-generate">Retry generation</PrimaryBtn>
      </div>
    );
  }

  return (
    <div className="text-center py-16">
      <div className="mx-auto w-24 h-24 rounded-full bg-[#E50914]/10 kd-pulse-ring flex items-center justify-center mb-8">
        <Loader2 size={32} className="animate-spin text-[#FF2A2D]" />
      </div>
      <h1 className="kd-display text-3xl md:text-5xl tracking-tight mb-3">Crafting your deck.</h1>
      <AnimatePresence mode="wait">
        <motion.p
          key={msgIdx}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          className="text-zinc-400"
          data-testid="gen-status"
        >
          {messages[msgIdx]}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}

// ============ Shared ============
function NavBtns({ onBack, onNext, disabled, nextLabel = "Continue", nextTest }) {
  return (
    <div className="mt-10 flex justify-between">
      <SecondaryBtn onClick={onBack} testId="wizard-back"><ArrowLeft size={14} /> Back</SecondaryBtn>
      <PrimaryBtn onClick={onNext} disabled={disabled} testId={nextTest}>{nextLabel} <ArrowRight size={14} /></PrimaryBtn>
    </div>
  );
}
function PrimaryBtn({ children, onClick, disabled, testId }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testId} className="rounded-full px-6 py-3 bg-[#E50914] hover:bg-[#FF2A2D] text-white transition-all duration-300 hover:shadow-[0_0_28px_rgba(229,9,20,0.5)] disabled:opacity-40 flex items-center gap-2">
      {children}
    </button>
  );
}
function SecondaryBtn({ children, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className="rounded-full px-5 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm transition-colors flex items-center gap-2">
      {children}
    </button>
  );
}
